import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, ArrowRight, Newspaper } from "lucide-react";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d MMMM yyyy", { locale: it });
    } catch {
      return dateStr;
    }
  };

  // Get featured news (first one) and rest
  const featuredNews = news[0];
  const gridNews = news.slice(1);

  return (
    <div className="min-h-screen bg-background" data-testid="news-page">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">
            Blog & News
          </h1>
          <p className="text-muted-foreground text-lg">
            Tutte le ultime notizie dal mondo delle sagre italiane
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[16/10] bg-muted rounded-xl mb-4" />
                <div className="h-4 bg-muted rounded w-1/3 mb-3" />
                <div className="h-6 bg-muted rounded w-full mb-2" />
                <div className="h-4 bg-muted rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : news.length > 0 ? (
          <>
            {/* Featured Article */}
            {featuredNews && (
              <Link to={`/news/${featuredNews.id}`} className="block mb-10 group">
                <Card className="overflow-hidden border-0 shadow-subtle hover:shadow-md transition-shadow">
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="aspect-[16/10] lg:aspect-auto lg:h-full overflow-hidden">
                      <img
                        src={featuredNews.immagine_url || "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800"}
                        alt={featuredNews.titolo}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6 lg:p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-4">
                        <Badge className="bg-primary/10 text-primary hover:bg-primary/20 rounded-md">
                          In Evidenza
                        </Badge>
                        <Badge variant="secondary" className="rounded-md">
                          {featuredNews.categoria || "News"}
                        </Badge>
                      </div>
                      
                      <h2 className="text-2xl lg:text-3xl font-semibold text-foreground mb-4 group-hover:text-primary transition-colors">
                        {featuredNews.titolo}
                      </h2>
                      
                      <p className="text-muted-foreground leading-relaxed mb-4 line-clamp-3">
                        {featuredNews.contenuto}
                      </p>
                      
                      <div className="flex items-center justify-between mt-auto">
                        <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          {formatDate(featuredNews.data_pubblicazione)}
                        </span>
                        <span className="text-primary font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                          Leggi di più <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </Link>
            )}

            {/* News Grid - 3 columns desktop, 1 column mobile */}
            {gridNews.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {gridNews.map((item) => (
                  <Link 
                    key={item.id} 
                    to={`/news/${item.id}`}
                    className="group"
                  >
                    <Card className="h-full overflow-hidden border-0 shadow-subtle hover:shadow-md transition-shadow" data-testid={`news-item-${item.id}`}>
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={item.immagine_url || "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400"}
                          alt={item.titolo}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="secondary" className="rounded-md text-xs">
                            {item.categoria || "News"}
                          </Badge>
                          {item.regione && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {item.regione}
                            </span>
                          )}
                        </div>
                        
                        <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                          {item.titolo}
                        </h3>
                        
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                          {item.contenuto}
                        </p>
                        
                        <div className="flex items-center justify-between pt-3 border-t border-border">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(item.data_pubblicazione)}
                          </span>
                          <span className="text-xs text-primary font-medium flex items-center gap-1 group-hover:gap-1.5 transition-all">
                            Leggi <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
              <Newspaper className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-medium text-foreground mb-2">
              Nessuna news disponibile
            </h3>
            <p className="text-muted-foreground">
              Le novità dal mondo delle sagre appariranno qui.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsPage;
